import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Interface cho thông tin log hoạt động
 */
export interface LogItem {
  /** ID duy nhất của log */
  id: number;
  /** Loại hoạt động */
  activityType: string;
  /** Mô tả chi tiết (tùy chọn) */
  description?: string;
  /** Mức độ log (INFO, WARN, ERROR, DEBUG) */
  logLevel: string;
  /** Kết quả của hoạt động (SUCCESS, FAILED, PENDING) */
  result: string;
  /** ID người dùng thực hiện (tùy chọn) */
  userId?: number;
  /** Thông tin người dùng (tùy chọn) */
  user?: { id: number; email?: string; fullName?: string };
  /** Module thực hiện hoạt động (tùy chọn) */
  module?: string;
  /** Hành động cụ thể (tùy chọn) */
  action?: string;
  /** Địa chỉ IP (tùy chọn) */
  ipAddress?: string;
  /** Thời gian tạo log */
  createdAt: string;
  /** Thông báo lỗi (tùy chọn) */
  errorMessage?: string;
}

/**
 * Interface cho tham số lấy danh sách logs admin
 */
export interface GetAdminLogsParams {
  /** Trang hiện tại (tùy chọn) */
  page?: number;
  /** Số lượng logs mỗi trang (tùy chọn) */
  limit?: number;
  /** Lọc theo loại hoạt động (tùy chọn) */
  activityType?: string;
  /** Lọc theo mức độ log (tùy chọn) */
  logLevel?: string;
  /** Lọc theo kết quả (tùy chọn) */
  result?: string;
  /** Lọc theo ID người dùng (tùy chọn) */
  userId?: number;
  /** Lọc theo module (tùy chọn) */
  module?: string;
  /** Lọc từ ngày (tùy chọn) */
  fromDate?: string;
  /** Lọc đến ngày (tùy chọn) */
  toDate?: string;
  /** Từ khóa tìm kiếm (tùy chọn) */
  search?: string;
}

/**
 * Interface cho phản hồi danh sách logs admin
 */
export interface GetAdminLogsResponse {
  /** Danh sách logs */
  data: LogItem[];
  /** Tổng số logs */
  total: number;
  /** Trang hiện tại */
  page: number;
  /** Số lượng logs mỗi trang */
  limit: number;
  /** Tổng số trang */
  totalPages: number;
}

/**
 * Interface cho thống kê logs
 */
export interface LogStatistics {
  /** Tổng số logs */
  totalLogs: number;
  /** Thống kê logs theo loại hoạt động */
  logsByType: { activityType: string; count: number }[];
  /** Thống kê logs theo mức độ */
  logsByLevel: { logLevel: string; count: number }[];
  /** Thống kê logs theo kết quả */
  logsByResult: { result: string; count: number }[];
  /** Số lượng logs lỗi */
  errorLogs: number;
}

/**
 * Interface cho phản hồi dọn dẹp logs
 */
export interface CleanupLogsResponse {
  /** Thông báo kết quả */
  message: string;
  /** Số lượng logs đã xóa */
  deletedCount: number;
  /** Số ngày giữ lại logs */
  daysToKeep: number;
}

/**
 * Service xử lý các chức năng quản lý logs hoạt động
 * Bao gồm xem, thống kê và dọn dẹp logs
 */
export class LogsService {
  /** URL cơ sở của API */
  private static baseUrl = getApiBase();

  /**
   * Lấy headers xác thực cho các request API
   * @returns Record<string, string> - Headers bao gồm Content-Type và Authorization
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  
  /**
   * Lấy danh sách logs admin với phân trang và lọc
   * @param params - Tham số lọc và phân trang
   * @returns Promise<GetAdminLogsResponse> - Danh sách logs với thông tin phân trang
   * @throws Error - Khi không thể tải danh sách logs
   */
  static async getAdminLogs(params: GetAdminLogsParams): Promise<GetAdminLogsResponse> {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
    });
    const response = await apiClient.get(`/admin/logs?${query.toString()}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải activity logs';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy chi tiết log theo ID
   * @param id - ID của log cần lấy
   * @returns Promise<LogItem> - Thông tin chi tiết log
   * @throws Error - Khi không thể tải chi tiết log
   */
  static async getAdminLogById(id: number): Promise<LogItem> {
    const response = await apiClient.get(`/admin/logs/${id}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải chi tiết log';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy thống kê tổng quan về logs
   * @param params - Tham số lọc theo khoảng thời gian
   * @returns Promise<LogStatistics> - Thống kê logs theo các tiêu chí
   * @throws Error - Khi không thể tải thống kê logs
   */
  static async getAdminLogStatistics(params: { fromDate?: string; toDate?: string }): Promise<LogStatistics> {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v) query.append(k, v);
    });
    const response = await apiClient.get(`/admin/logs/statistics/overview?${query.toString()}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải thống kê logs';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Dọn dẹp logs cũ để tiết kiệm dung lượng
   * @param daysToKeep - Số ngày giữ lại logs (mặc định 90 ngày)
   * @returns Promise<CleanupLogsResponse> - Kết quả dọn dẹp logs
   * @throws Error - Khi không thể dọn dẹp logs
   */
  static async cleanupAdminLogs(daysToKeep: number = 90): Promise<CleanupLogsResponse> {
    const response = await apiClient.post('/admin/logs/cleanup', { daysToKeep });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể dọn dẹp logs';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }
}
